import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { EASE_OUT, SPRING_POP, SPRING_SETTLE } from '../../shared/motion.ts'
import { useCountUp } from './useCountUp'
import { GameHeader } from './components/GameHeader'
import { BottomSheetDrawer, type SheetTab } from './components/BottomSheetDrawer'
import { BetDock } from './components/BetDock'
import brandMark from '../../shared/assets/snowfold-atlas-mark.png'
import blastHazard from '../../shared/assets/blast-rupture.webp'
import blastStage9 from '../../shared/assets/blast-world-9.webp'
import blastStage6 from '../../shared/assets/blast-world-6.webp'
import {
  fetchBalance,
  fetchBlastTables,
  getInitData,
  loginDev,
  loginWithInitData,
  readTierFromUrl,
  takeTokenFromUrl,
  writeTierToUrl,
  type BlastTable,
} from './api'
import { goLobby } from './lobby'
import { useBlastSocket } from './useBlastSocket'

const QUICK = [50, 100, 250, 500]

const HOTSPOTS_9 = [
  { x: 23.48, y: 19.98 },
  { x: 50.17, y: 18.78 },
  { x: 77.73, y: 20.76 },
  { x: 21.89, y: 49.91 },
  { x: 50.52, y: 53.31 },
  { x: 78.94, y: 50.18 },
  { x: 19.75, y: 80.29 },
  { x: 50.38, y: 82 },
  { x: 80.56, y: 80.52 },
] as const

const HOTSPOTS_6 = [
  { x: 23.55, y: 34.85 },
  { x: 50.03, y: 32.73 },
  { x: 76.9, y: 34.9 },
  { x: 19.54, y: 71.96 },
  { x: 49.9, y: 74.63 },
  { x: 80.11, y: 72.01 },
] as const

/** Map image-space % into the visible object-fit:cover frame. */
function mapCoverPoint(
  imgX: number,
  imgY: number,
  boxW: number,
  boxH: number,
  imgW: number,
  imgH: number,
): { x: number; y: number } {
  if (boxW <= 0 || boxH <= 0 || imgW <= 0 || imgH <= 0) {
    return { x: imgX, y: imgY }
  }
  const boxR = boxW / boxH
  const imgR = imgW / imgH
  let vx = 0
  let vy = 0
  let vw = 1
  let vh = 1
  if (imgR > boxR) {
    vw = boxR / imgR
    vx = (1 - vw) / 2
  } else if (imgR < boxR) {
    vh = imgR / boxR
    vy = (1 - vh) / 2
  }
  return {
    x: Math.min(92, Math.max(8, ((imgX / 100 - vx) / vw) * 100)),
    y: Math.min(92, Math.max(8, ((imgY / 100 - vy) / vh) * 100)),
  }
}

export default function App() {
  const { t, i18n } = useTranslation()
  useEffect(() => {
    document.title = t('title')
  }, [t, i18n.language])

  const [token, setToken] = useState<string | null>(() => takeTokenFromUrl())
  const [balance, setBalance] = useState(0)
  const [authLoading, setAuthLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authRetry, setAuthRetry] = useState(0)
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null)
  const [amount, setAmount] = useState(100)
  const [tier, setTier] = useState(() => readTierFromUrl())
  const [tables, setTables] = useState<BlastTable[]>([])
  const [sheetOpen, setSheetOpen] = useState(false)
  const [showChips, setShowChips] = useState(false)
  const [pendingAction, setPendingAction] = useState<'bet' | 'room' | null>(null)
  const pendingActionRef = useRef<'bet' | 'room' | null>(null)
  const [pendingRoomId, setPendingRoomId] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [balanceWarning, setBalanceWarning] = useState<string | null>(null)
  const [balanceRefreshing, setBalanceRefreshing] = useState(false)
  const [balanceSyncedRevision, setBalanceSyncedRevision] = useState(0)
  const balanceRefreshRef = useRef(false)
  const gridRef = useRef<HTMLDivElement>(null)
  const stageImgRef = useRef<HTMLImageElement>(null)
  const [mappedHotspots, setMappedHotspots] = useState<{ x: number; y: number }[]>(
    () => [...HOTSPOTS_9],
  )

  const reduced = useReducedMotion() ?? false
  const displayBalance = useCountUp(balance)
  const { snap, placeBet, changeRoom } = useBlastSocket(token, tier)
  const balanceNeedsSync = snap.balanceSyncRevision > balanceSyncedRevision
  const settlementBlocked = snap.settlementIssue != null
  const settlementMessage = snap.settlementIssue?.roundId
    ? t('settlementReconciliationRound', { roundId: snap.settlementIssue.roundId })
    : t('settlementReconciliation')
  const displayPayout = useCountUp(snap.lastResult?.payout ?? 0)
  const betMaxRef = useRef(0)
  const latestRoundStatusRef = useRef({ phase: snap.phase, bettingEndsIn: snap.bettingEndsIn })
  const [liveRoundStatus, setLiveRoundStatus] = useState('')
  const prevRound = useRef<string | null>(null)
  const roomStats = Array.isArray(snap.roomStats) ? snap.roomStats : []
  const exploded = Array.isArray(snap.exploded) ? snap.exploded : []
  const fallbackRoomCount =
    tables.find((x) => x.id === tier)?.room_count ??
    (tier === 'high_risk_6' ? 6 : 9)
  const rooms =
    Array.isArray(snap.rooms) && snap.rooms.length > 0
      ? snap.rooms
      : Array.from({ length: fallbackRoomCount }, (_, i) => ({ id: i + 1, odds: 0 }))
  const sixRoomStage = rooms.length === 6
  const blastStage = sixRoomStage ? blastStage6 : blastStage9
  const stageLabel = sixRoomStage ? t('stageSix') : t('stageNine')
  const roomHotspots = sixRoomStage ? HOTSPOTS_6 : HOTSPOTS_9
  const demoBridgeRef = useRef<{ ready: boolean; stop?: () => void } | null>(null)
  useEffect(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      return () => {
        demoBridgeRef.current?.stop?.()
        demoBridgeRef.current = null
      }
    }
  }, [])
  useEffect(() => {
    if (import.meta.env.VITE_DEMO_MODE === 'true') {
      const initializationError = authError ? 'auth_failed' : (undefined)
      const usable = Boolean(token) && !authLoading && !authError && (snap.connected && tables.length > 0)
      const previous = demoBridgeRef.current
      // Latch the first usable UI: ordinary quote/table/round updates must not drop host messages.
      if (previous?.ready || (!usable && previous)) return
      if (!initializationError && !usable) return
      previous?.stop?.()
      const pending: { ready: boolean; stop?: () => void } = { ready: usable }
      demoBridgeRef.current = pending
      void import('../../shared/demo/iframeBridge.ts').then(({ startDemoIframeBridge }) => {
        if (demoBridgeRef.current !== pending) return
        pending.stop = startDemoIframeBridge({
          game: 'blast-rooms',
          locale: i18n.language,
          getLocale: () => i18n.language,
          setLocale: (locale) => i18n.changeLanguage(locale),
          error: initializationError,
        })
      }).catch(() => {
        if (demoBridgeRef.current === pending) demoBridgeRef.current = null
        // A bridge chunk failure leaves the host's bounded timeout in charge.
      })
    }
  }, [authLoading, authError, token, i18n, snap.connected, tables.length])

  const statsMap = useMemo(
    () => new Map(roomStats.map((s) => [s.id, s])),
    [roomStats],
  )
  latestRoundStatusRef.current = {
    phase: snap.phase,
    bettingEndsIn: snap.bettingEndsIn,
  }

  useEffect(() => {
    const update = () => {
      const current = latestRoundStatusRef.current
      if (current.phase === 'betting' && current.bettingEndsIn != null) {
        setLiveRoundStatus(t('bettingLive', { sec: Math.ceil(current.bettingEndsIn) }))
      } else {
        setLiveRoundStatus(t(`phase.${current.phase}`))
      }
    }
    update()
    if (snap.phase !== 'betting') return
    const id = window.setInterval(update, 1000)
    return () => window.clearInterval(id)
  }, [snap.phase, snap.roundId, t])

  useEffect(() => {
    let cancelled = false
    setAuthLoading(true)
    setAuthError(null)
    void (async () => {
      try {
        if (token) {
          const { balance: bal } = await fetchBalance(token)
          if (cancelled) return
          setBalance(bal)
          setAuthLoading(false)
          return
        }
        const init = await getInitData()
        const res = init ? await loginWithInitData(init) : await loginDev()
        if (cancelled) return
        setToken(res.token)
        setBalance(res.user.balance)
        setAuthLoading(false)
      } catch (e) {
        if (!cancelled) {
          void e
          setAuthError(t('authFailed'))
          setAuthLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authRetry])

  useEffect(() => {
    if (!token) return
    let cancelled = false
    void (async () => {
      try {
        const res = await fetchBlastTables(token)
        if (!cancelled) setTables(res.tables)
      } catch {
        /* ignore */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  const refreshBalance = useCallback(async (syncRevision?: number) => {
    if (!token || balanceRefreshRef.current) return false
    balanceRefreshRef.current = true
    setBalanceRefreshing(true)
    try {
      const res = await fetchBalance(token)
      setBalance(res.balance)
      if (syncRevision != null) {
        setBalanceSyncedRevision((current) => Math.max(current, syncRevision))
      }
      setBalanceWarning(null)
      return true
    } catch {
      setBalanceWarning(t('balanceRefreshFailed'))
      return false
    } finally {
      balanceRefreshRef.current = false
      setBalanceRefreshing(false)
    }
  }, [t, token])

  useEffect(() => {
    if (!balanceNeedsSync || balanceWarning || balanceRefreshing) return
    void refreshBalance(snap.balanceSyncRevision)
  }, [
    balanceNeedsSync,
    balanceRefreshing,
    balanceWarning,
    refreshBalance,
    snap.balanceSyncRevision,
  ])

  useEffect(() => {
    if (snap.myBet) setSelectedRoomId(snap.myBet.roomId)
    if (!pendingAction) return
    const acknowledged =
      pendingAction === 'bet'
        ? Boolean(snap.myBet)
        : pendingRoomId != null && snap.myBet?.roomId === pendingRoomId
    if (
      acknowledged ||
      snap.lastError ||
      settlementBlocked ||
      !snap.connected ||
      snap.phase !== 'betting'
    ) {
      pendingActionRef.current = null
      setPendingAction(null)
      setPendingRoomId(null)
    }
  }, [
    pendingAction,
    pendingRoomId,
    settlementBlocked,
    snap.connected,
    snap.lastError,
    snap.myBet,
    snap.phase,
  ])

  useEffect(() => {
    if (snap.roundId && snap.roundId !== prevRound.current) {
      prevRound.current = snap.roundId
      if (!snap.myBet) setSelectedRoomId(null)
      betMaxRef.current = 0
    }
  }, [snap.roundId, snap.myBet])

  useEffect(() => {
    const src = sixRoomStage ? HOTSPOTS_6 : HOTSPOTS_9
    const grid = gridRef.current
    const img = stageImgRef.current
    if (!grid || !img) {
      setMappedHotspots([...src])
      return
    }
    const remap = () => {
      const imgW = img.naturalWidth || 1448
      const imgH = img.naturalHeight || 1086
      setMappedHotspots(
        src.map((p) =>
          mapCoverPoint(p.x, p.y, grid.clientWidth, grid.clientHeight, imgW, imgH),
        ),
      )
    }
    remap()
    const ro = new ResizeObserver(remap)
    ro.observe(grid)
    img.addEventListener('load', remap)
    return () => {
      ro.disconnect()
      img.removeEventListener('load', remap)
    }
  }, [sixRoomStage, blastStage, authLoading])

  const onSelectTier = (nextTier: string) => {
    if (nextTier === tier || snap.myBet || settlementBlocked || pendingActionRef.current) return
    setTier(nextTier)
    writeTierToUrl(nextTier)
    setSelectedRoomId(null)
  }

  const highlightId = selectedRoomId ?? snap.myBet?.roomId ?? null

  const onPickRoom = (id: number) => {
    if (
      !snap.connected ||
      settlementBlocked ||
      snap.phase !== 'betting' ||
      pendingActionRef.current
    ) return
    if (snap.myBet && snap.myBet.roomId !== id) {
      setActionError(null)
      const sent = changeRoom(id)
      if (!sent) {
        setActionError(t('connectionUnavailable'))
        return
      }
      pendingActionRef.current = 'room'
      setPendingAction('room')
      setPendingRoomId(id)
      return
    }
    setSelectedRoomId(id)
  }

  const onConfirm = () => {
    if (
      !snap.connected ||
      pendingActionRef.current ||
      snap.phase !== 'betting' ||
      snap.myBet ||
      settlementBlocked ||
      balanceNeedsSync ||
      balanceWarning ||
      balanceRefreshing ||
      selectedRoomId == null
    ) return
    setActionError(null)
    const sent = placeBet(amount, selectedRoomId)
    if (!sent) {
      setActionError(t('connectionUnavailable'))
      return
    }
    pendingActionRef.current = 'bet'
    setPendingAction('bet')
  }

  if (authLoading) {
    return (
      <div className="center" role="status" aria-live="polite">
        <div className="spinner" aria-hidden />
        <p>{t('connecting')}</p>
      </div>
    )
  }
  if (authError || !token) {
    return (
      <div className="center" role="alert">
        <p className="error">{authError ?? t('unauthorized')}</p>
        <button type="button" className="btn-lobby" onClick={() => setAuthRetry((n) => n + 1)}>
          {t('retryLogin')}
        </button>
        <button type="button" className="btn-lobby" onClick={() => goLobby(token)}>
          {t('backLobby')}
        </button>
      </div>
    )
  }

  const revealed = snap.phase === 'revealing' || snap.phase === 'settled'

  let statusLabel = t(`phase.${snap.phase}`)
  if (snap.phase === 'betting' && snap.bettingEndsIn != null) {
    statusLabel = `${statusLabel} · ${snap.bettingEndsIn.toFixed(1)}s`
  }

  if (snap.phase === 'betting' && snap.bettingEndsIn != null) {
    betMaxRef.current = Math.max(betMaxRef.current, snap.bettingEndsIn)
  } else if (snap.phase !== 'betting') {
    betMaxRef.current = 0
  }
  const betPct =
    snap.phase === 'betting' && snap.bettingEndsIn != null && betMaxRef.current > 0
      ? Math.max(0, Math.min(1, snap.bettingEndsIn / betMaxRef.current))
      : 0

  const canBet =
    snap.phase === 'betting' &&
    !snap.myBet &&
    !settlementBlocked &&
    selectedRoomId != null &&
    amount >= 10 &&
    amount <= balance &&
    snap.connected &&
    !balanceWarning &&
    !balanceNeedsSync &&
    !balanceRefreshing &&
    !pendingAction

  let primaryLabel = t('bet')
  if (settlementBlocked) {
    primaryLabel = t('settlementNeedsReview')
  } else if (!snap.connected) {
    primaryLabel = t('reconnecting')
  } else if (pendingAction === 'bet') {
    primaryLabel = t('submittingBet')
  } else if (pendingAction === 'room') {
    primaryLabel = t('switchingRoom')
  } else if (balanceRefreshing) {
    primaryLabel = t('refreshingBalance')
  } else if (balanceWarning) {
    primaryLabel = t('retryBalance')
  } else if (balanceNeedsSync) {
    primaryLabel = t('refreshingBalance')
  } else if (snap.myBet) {
    primaryLabel = t('placed', { amount: snap.myBet.amount })
  } else if (!highlightId) {
    primaryLabel = t('selectRoom')
  }

  const drawerTabs: SheetTab[] = [
    {
      id: 'rules',
      label: t('rules'),
      content: (
        <div style={{ lineHeight: 1.6 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#fff' }}>{t('rulesTitle')}</h3>
          <p style={{ color: 'var(--text-secondary)' }}>{t('rulesBody')}</p>
        </div>
      ),
    },
    {
      id: 'stats',
      label: t('tables'),
      content: (
        <div style={{ lineHeight: 1.6 }}>
          {snap.presence ? (
            <p className="presence" style={{ marginBottom: 12 }}>
              {t('presence', {
                seated: snap.presence.realPlayers + snap.presence.bots,
                watching: snap.presence.watching,
                pot: snap.presence.totalBet,
              })}
            </p>
          ) : null}
        </div>
      ),
    },
  ]

  const bettingControlsDisabled =
    !snap.connected ||
    Boolean(pendingAction) ||
    snap.phase !== 'betting' ||
    Boolean(snap.myBet) ||
    settlementBlocked ||
    balanceNeedsSync ||
    Boolean(balanceWarning) ||
    balanceRefreshing

  // Tier 1: Table & Room selection
  const parameterSlot = (
    <div className="bet-dock-segmented">
      {(tables.length > 0
        ? tables
        : [
            { id: 'classic_9' },
            { id: 'high_risk_6' },
          ]
      ).map((item) => (
        <button
          key={item.id}
          type="button"
          className={`bet-dock-segment-btn${tier === item.id ? ' active' : ''}`}
          disabled={Boolean(snap.myBet) || settlementBlocked || Boolean(pendingAction)}
          aria-pressed={tier === item.id}
          onClick={() => onSelectTier(item.id)}
        >
          {t(`tier.${item.id}`, { defaultValue: item.id })}
        </button>
      ))}
    </div>
  )

  // Tier 2: Stake input & Collapsible Quick chips
  const amountSlot = (
    <div className="bet-dock-tier-amount">
      <div className="bet-dock-amount-main-row">
        <div className="bet-dock-input-group">
          <label htmlFor="blast-amount">{t('bet')}</label>
          <input
            id="blast-amount"
            type="number"
            min={10}
            max={balance}
            value={amount}
            disabled={bettingControlsDisabled}
            onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
          />
        </div>
        <div className="bet-dock-quick-multipliers">
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={bettingControlsDisabled}
            onClick={() => setAmount(Math.max(10, Math.floor(amount / 2)))}
          >
            1/2
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={bettingControlsDisabled}
            onClick={() => setAmount(Math.max(10, Math.min(balance, amount * 2)))}
          >
            2X
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={bettingControlsDisabled}
            onClick={() => setAmount(Math.max(10, balance))}
          >
            {t('max')}
          </button>
          <button
            type="button"
            className={`bet-dock-chip-toggle${showChips ? ' active' : ''}`}
            disabled={bettingControlsDisabled}
            onClick={() => setShowChips(!showChips)}
            title={t('quickChips')}
            aria-label={t('quickChips')}
            aria-expanded={showChips}
            aria-controls="blast-quick-chips"
          >
            {showChips ? '▲' : '⚡'}
          </button>
        </div>
      </div>
      {showChips && (
        <div id="blast-quick-chips" className="bet-dock-collapsible-chips">
          {QUICK.map((q) => (
            <button
              key={q}
              type="button"
              className="bet-dock-preset-chip"
              disabled={bettingControlsDisabled}
              onClick={() => {
                setAmount(q)
                setShowChips(false)
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  // Tier 3: Primary Action CTA
  const ctaSlot = (
    <motion.button
      type="button"
      className={`bet-dock-cta-btn state-ready${snap.myBet ? ' state-cashout' : ''}`}
      disabled={
        balanceWarning && !settlementBlocked
          ? balanceRefreshing || !snap.connected
          : !canBet
      }
      onClick={() => {
        if (balanceWarning && !settlementBlocked) void refreshBalance(snap.balanceSyncRevision)
        else onConfirm()
      }}
      whileTap={reduced || !canBet ? undefined : { scale: 0.98 }}
      transition={SPRING_SETTLE}
    >
      {primaryLabel}
    </motion.button>
  )

  const metaSlot = (
    <>
      <span>{t('balance', { amount: Math.round(balance) })}</span>
      <span>
        {highlightId ? t('selectedRoom', { id: highlightId }) : statusLabel}
      </span>
    </>
  )

  return (
    <div className="app app-single-viewport" data-game="blast">
      <GameHeader
        title={t('title')}
        brandSrc={brandMark}
        lobbyLabel={t('backLobby')}
        onLobby={() => goLobby(token)}
        balance={displayBalance}
        balanceLabel={t('balance', { amount: Math.round(displayBalance) })}
        connected={snap.connected}
        connectedLabel={t('connected')}
        disconnectedLabel={t('reconnecting')}
        onOpenRules={() => setSheetOpen(true)}
      />

      {snap.phase === 'betting' && snap.bettingEndsIn != null && (
        <div style={{ width: '100%', height: 3, background: 'rgba(255, 255, 255, 0.1)', overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              background: betPct <= 0.25 ? 'var(--color-danger, #ff5e5e)' : 'var(--color-primary, #4ea8de)',
              transform: `scaleX(${betPct})`,
              transformOrigin: 'left center',
              transition: 'transform 0.1s linear',
            }}
          />
        </div>
      )}

      <div className="game-stage-viewport">
        <div
          ref={gridRef}
          className={[
            'grid',
            revealed ? 'revealing' : '',
            snap.phase === 'locked' ? 'locked' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ width: '100%', maxHeight: '100%', position: 'relative' }}
          data-n={rooms.length}
          data-tier={sixRoomStage ? 'six' : 'nine'}
          role="group"
          aria-label={t('title')}
        >
          <img
            ref={stageImgRef}
            key={sixRoomStage ? 'stage-six' : 'stage-nine'}
            className="blast-stage-image"
            src={blastStage}
            width={1448}
            height={1086}
            decoding="async"
            fetchPriority="high"
            alt=""
            aria-hidden
          />
          <div className="blast-stage-hud" aria-hidden>
            <span>{stageLabel}</span>
            <strong>{statusLabel}</strong>
          </div>
          {rooms.map((room) => {
            const st = statsMap.get(room.id)
            const isBoom = exploded.includes(room.id)
            const selected = highlightId === room.id
            return (
              <RoomTile
                key={`${snap.roundId ?? 'x'}-${room.id}`}
                odds={room.odds}
                selected={selected}
                revealed={revealed}
                isBoom={isBoom}
                boomOrder={isBoom ? exploded.indexOf(room.id) : 0}
                betting={
                  snap.connected &&
                  !pendingAction &&
                  !settlementBlocked &&
                  snap.phase === 'betting'
                }
                reduced={reduced}
                labelRoom={t('room', { id: room.id })}
                labelPlayers={t('players', { n: st?.players ?? 0 })}
                labelState={
                  revealed
                    ? isBoom
                      ? t('roomExploded')
                      : t('roomSafe')
                    : selected
                      ? t('roomSelected')
                      : t('roomAvailable')
                }
                labelOdds={
                  room.odds > 0
                    ? t('roomOdds', { odds: room.odds.toFixed(2) })
                    : t('roomOddsPending')
                }
                position={mappedHotspots[room.id - 1] ?? roomHotspots[room.id - 1]}
                onPick={() => onPickRoom(room.id)}
              />
            )
          })}
        </div>
        {snap.settlementIssue ? (
          <div className="blast-settlement-alert" role="alert" aria-live="assertive">
            <strong>{t('settlementNeedsReview')}</strong>
            <span>{settlementMessage}</span>
          </div>
        ) : null}
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {liveRoundStatus}
        </span>
        <AnimatePresence>
          {snap.lastResult ? (
            <motion.div
              key={`res-${snap.roundId}-${snap.lastResult.won}`}
              className={`result-overlay ${snap.lastResult.won ? 'win' : 'lose'}`}
              role="status"
              aria-live="polite"
              style={{
                position: 'absolute',
                zIndex: 12,
                left: 12,
                right: 12,
                bottom: 12,
                pointerEvents: 'none',
              }}
              initial={reduced ? false : { opacity: 0, scale: 0.92, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={reduced ? undefined : { opacity: 0, scale: 0.96 }}
              transition={reduced ? { duration: 0 } : SPRING_POP}
            >
              <span className="result-label">
                {snap.lastResult.won
                  ? t('youWin', { payout: Math.round(displayPayout) })
                  : t('youLose', { amount: snap.lastResult.amount })}
              </span>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      <BetDock
        className="blast-bet-dock"
        parameterSlot={parameterSlot}
        amountSlot={amountSlot}
        ctaSlot={ctaSlot}
        metaSlot={metaSlot}
        error={
          (settlementBlocked ? settlementMessage : null) ??
          actionError ??
          (snap.lastError ? t('requestFailed') : null) ??
          (!snap.connected ? t('connectionUnavailable') : null) ??
          balanceWarning
        }
      />

      <BottomSheetDrawer
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        tabs={drawerTabs}
        closeLabel={t('close')}
      />
    </div>
  )
}

type RoomTileProps = {
  odds: number
  selected: boolean
  revealed: boolean
  isBoom: boolean
  boomOrder: number
  betting: boolean
  reduced: boolean
  labelRoom: string
  labelPlayers: string
  labelState: string
  labelOdds: string
  position: { x: number; y: number } | undefined
  onPick: () => void
}

function RoomTile({
  odds,
  selected,
  revealed,
  isBoom,
  boomOrder,
  betting,
  reduced,
  labelRoom,
  labelPlayers,
  labelState,
  labelOdds,
  position,
  onPick,
}: RoomTileProps) {
  const oddsDisplay = useCountUp(odds > 0 ? odds : 0, 280)
  const boomDelay = boomOrder * 0.15
  const className = [
    'room',
    selected ? 'selected' : '',
    betting ? 'betting' : '',
    revealed && isBoom ? 'exploded' : '',
    revealed && !isBoom ? 'safe' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <motion.button
      type="button"
      className={className}
      style={
        {
          '--room-x': `${position?.x ?? 50}%`,
          '--room-y': `${position?.y ?? 50}%`,
          '--boom-delay': `${boomDelay}s`,
          '--blast-impact-shift-x': '0px',
          '--blast-impact-shift-y': '0px',
        } as CSSProperties
      }
      aria-label={`${labelRoom}, ${labelState}, ${labelOdds}, ${labelPlayers}`}
      aria-pressed={selected}
      disabled={!betting}
      onClick={onPick}
    >
      <div className="room-copy">
        <div className="room-id">{labelRoom}</div>
        {odds > 0 ? (
          <div className="room-odds">{oddsDisplay.toFixed(2)}x</div>
        ) : (
          <div className="room-odds muted">--</div>
        )}
        <div className="room-meta">{labelPlayers}</div>
      </div>
      <AnimatePresence>
        {revealed && isBoom ? (
          <motion.img
            className="blast-hazard blast-hazard--core"
            src={blastHazard}
            alt=""
            aria-hidden
            initial={reduced ? false : { opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduced ? undefined : { opacity: 0, scale: 0.9 }}
            transition={
              reduced
                ? { duration: 0 }
                : { delay: boomDelay, duration: 0.32, ease: EASE_OUT }
            }
          />
        ) : null}
      </AnimatePresence>
    </motion.button>
  )
}
